import {useEffect,useRef,useState} from 'react';
import {BrowserRouter,Navigate,Route,Routes,useLocation,useNavigate,useParams} from 'react-router-dom';
import AppShell from './components/AppShell';
import ShareGate from './components/ShareGate';
import {SignIn,Signup} from './screens/AuthScreens';
import Introduction from './screens/Introduction';
import Headquarters from './screens/Headquarters';
import Website from './screens/Website';
import Tasks from './screens/Tasks';
import ChatModule from './screens/ChatModule';
import Inbox from './screens/Inbox';
import Files from './screens/Files';
import Settings from './screens/Settings';
import Bots from './screens/Bots';
import Models from './screens/Models';
import Marketplace from './screens/Marketplace';
import Projects from './screens/Projects';
import Calendar from './screens/Calendar';
import {applyTheme,defaultTheme} from './theme/tokens';
import {dataClient} from './data/dataClient';
import {resolveWorkspaceRedirect} from './services/viewerSession';
import './App.css';

// Dev-only escape hatch: `npm run dev?demo=1` (or localStorage
// command-center-demo=1) shows the mock Northstar workspace WITHOUT PocketBase.
// NEVER active in a production build (import.meta.env.DEV is false) — the live
// app always requires a real OTP session (owner decision, docs/deploy_fixes.md).
const demoEnabled = () => import.meta.env?.DEV && (
  new URLSearchParams(window.location.search).has('demo')
  || window.localStorage.getItem('command-center-demo') === '1'
);

// 14 screen slugs → element. Screen resolution for /workspace/:slug/:screen
// (screen-capability spec + cross-screen-overview-and-live-share.md §2.3).
// finance/ads share the ChatModule screen bound to their module.
const SCREENS={
  introduction: <Introduction />,
  headquarters: <Headquarters />,
  website: <Website />,
  tasks: <Tasks />,
  finance: <ChatModule module="finance" />,
  ads: <ChatModule module="ads" />,
  inbox: <Inbox />,
  files: <Files />,
  settings: <Settings />,
  bots: <Bots />,
  models: <Models />,
  marketplace: <Marketplace />,
  projects: <Projects />,
  calendar: <Calendar />
};

function BootScreen() {
  return (
    <div className="boot-screen" aria-busy="true">
      <span className="pulse-ring">C</span>
      <b>Loading your workspace…</b>
    </div>
  );
}

// Resolves the owner's companySlug for URLs once a session exists. Post-T1 the
// PB business and the demo business both carry a slug; 'workspace' is only the
// pre-auth placeholder (viewerSession.js resolveWorkspaceRedirect semantics).
function ownerSlug() {
  return dataClient.peek()?.business?.slug || 'workspace';
}

// Session boot: mirrors the old ProtectedShell lifecycle — demo escape hatch,
// dataClient.initSession(), onAuthChange subscription. State: null (booting) →
// {authenticated, workspace}.
function useSession() {
  const [session,setSession]=useState(null);

  useEffect(()=> {
    let cancelled=false;

    async function boot() {
      if (demoEnabled()) {
        dataClient.enterDemo();
        if (!cancelled) setSession({authenticated:true,workspace:'demo'});
        return;
      }
      const result=await dataClient.initSession();
      if (!cancelled) setSession({authenticated:result.authenticated,workspace:result.workspace});
    }

    const unsubscribe=dataClient.onAuthChange(({authenticated})=> {
      // Refresh events keep the resolved workspace; only auth flips.
      setSession((prev)=> prev ? {...prev,authenticated} : prev);
    });

    boot();
    return ()=> { cancelled=true; unsubscribe(); };
  },[]);

  return session;
}

// Workspace gate for /workspace/:companySlug/*. Owner session whose slug
// matches → full chrome (AppShell + nested screen). Everyone else — guests,
// and owners of a *different* tenant — sees the ShareGate (viewer credential
// exchange lands in T13).
function WorkspaceGate({session,installPrompt,onInstall}) {
  const {companySlug,screenSlug}=useParams();
  const isOwner=session?.authenticated && companySlug===ownerSlug();

  if (!isOwner) {
    return <ShareGate companySlug={companySlug} screenSlug={screenSlug} />;
  }

  return (
    <AppShell
      installPrompt={installPrompt}
      onInstall={onInstall}
      workspace={session.workspace}
      companySlug={companySlug}
    />
  );
}

// Nested screen renderer — resolves :screenSlug, canonicalising unknown slugs
// to headquarters (fallback), never rendering a dead screen.
function WorkspaceScreen() {
  const {companySlug,screenSlug}=useParams();
  const screen=SCREENS[screenSlug];

  if (!screen) {
    return <Navigate to={`/workspace/${companySlug || ownerSlug()}/headquarters`} replace />;
  }

  return screen;
}

// Bare '/', unknown paths. Authed → their headquarters; guests → /signin.
function RootRedirect({session}) {
  const navigate=useNavigate();

  useEffect(()=> {
    if (session?.authenticated) {
      navigate(`/workspace/${ownerSlug()}/headquarters`,{replace:true});
    } else {
      navigate('/signin',{replace:true});
    }
  },[session,navigate]);

  return null;
}

// One-shot legacy-hash boot translation: '#/finance' style bookmarks become
// /workspace/{slug}/finance the first time the session resolves. Auth-route
// hashes ('#/signin') resolve through the same helper to their plain URLs.
function LegacyHashBridge({session}) {
  const navigate=useNavigate();
  const location=useLocation();
  const done=useRef(false);

  useEffect(()=> {
    if (!session || done.current) return;
    done.current=true;
    const hash=window.location.hash;
    if (!hash || hash==='#' || hash==='#/') return;
    const target=resolveWorkspaceRedirect(hash,session.authenticated ? ownerSlug() : 'workspace');
    if (target && target!==location.pathname) {
      navigate(target,{replace:true});
    }
  },[session,navigate,location.pathname]);

  return null;
}

function AppRouter({installPrompt,onInstall}) {
  const session=useSession();

  if (session===null) return <BootScreen />;

  return (
    <>
      <LegacyHashBridge session={session} />
      <Routes>
        <Route path="/signin" element={<SignIn installPrompt={installPrompt} onInstall={onInstall} />} />
        <Route path="/signup" element={<Signup installPrompt={installPrompt} onInstall={onInstall} />} />
        <Route path="/workspace/:companySlug" element={<WorkspaceGate session={session} installPrompt={installPrompt} onInstall={onInstall} />}>
          <Route index element={<Navigate to="headquarters" replace />} />
          <Route path=":screenSlug" element={<WorkspaceScreen />} />
        </Route>
        <Route path="*" element={<RootRedirect session={session} />} />
      </Routes>
    </>
  );
}

export default function App() {
  const [installPrompt,setInstallPrompt]=useState(null);

  useEffect(()=> {
    const override=dataClient.peek().business.theme;
    applyTheme({
      ...defaultTheme,
      colors: {...defaultTheme.colors,...(override?.colors || {})}
    });

    const handler=(event)=> {
      event.preventDefault();
      setInstallPrompt(event);
    };

    window.addEventListener('beforeinstallprompt',handler);

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register(`${import.meta.env.BASE_URL}sw.js`, { scope: import.meta.env.BASE_URL })
        .catch(() => { /* registration is best-effort; the app works without it */ });
    }

    return ()=> window.removeEventListener('beforeinstallprompt',handler);
  },[]);

  async function install() {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  }

  return (
    <BrowserRouter>
      <AppRouter installPrompt={installPrompt} onInstall={install} />
    </BrowserRouter>
  );
}
